import type {
  PayloadControlChange,
  IDynamicControlSetting,
  IOnControlChangeResult,
} from '../types';
import type { EControlSettingType } from '../../type';

import {
  onControlChange,
  onControlUIChange,
  emitControlChange,
  emitControlUIChange,
  emitDeleteControlUI,
  emitResetControlData,
  onDeleteControlUI,
  onResetControlData,
} from '../types';
import { useControlSettingStore } from '../../ReportFilter/stores';
import { useEffect, useRef } from 'react';

interface IControlDataChangeProps {
  type: EControlSettingType;
  controlChange?: (data: PayloadControlChange) => void;
  controlUIChange?: (data: IDynamicControlSetting, type: EControlSettingType) => void;
  deleteControlUI?: (id: string, type: EControlSettingType) => void;
  resetControlData?: (ids: string[], type: EControlSettingType) => void;
}

interface IControlDataChange {
  emitControlChange: (data: PayloadControlChange) => void;
  emitControlUIChange: (data: IDynamicControlSetting) => void;
  emitDeleteControlUI: (id: string) => void;
  emitResetControlData: (id: string[]) => void;
  onControlChange: (cb: (p: PayloadControlChange) => void) => IOnControlChangeResult;
  onControlUIChange: (cb: (p: IDynamicControlSetting) => void) => IOnControlChangeResult;
  onDeleteControlUI: (cb: (id: string) => void) => IOnControlChangeResult;
  onResetControlData: (cb: (id: string[]) => void) => IOnControlChangeResult;
}

export const useControlDataChange = (props?: IControlDataChangeProps): IControlDataChange => {
  const { type, controlChange, controlUIChange, deleteControlUI, resetControlData } = props ?? {};

  const controlSettingUI = useControlSettingStore((state) => state.controlSettingUI);

  const callbackRefs = useRef({
    controlChangeRef: controlChange,
    controlUIChangeRef: controlUIChange,
    deleteControlUIRef: deleteControlUI,
    resetControlDataRef: resetControlData,
  });
  callbackRefs.current = {
    controlChangeRef: controlChange,
    controlUIChangeRef: controlUIChange,
    deleteControlUIRef: deleteControlUI,
    resetControlDataRef: resetControlData,
  };

  useEffect(() => {
    const { controlChangeRef, controlUIChangeRef, deleteControlUIRef, resetControlDataRef } =
      callbackRefs.current;
    const cleanups: IOnControlChangeResult[] = [];

    // Access callbackRefs.current INSIDE callbacks to get latest value at execution time
    if (controlChangeRef) {
      cleanups.push(onControlChange((data) => callbackRefs.current.controlChangeRef?.(data)));
    }
    if (controlUIChangeRef && type) {
      cleanups.push(
        onControlUIChange((data) => callbackRefs.current.controlUIChangeRef?.(data, type)),
      );
    }
    if (deleteControlUIRef && type) {
      cleanups.push(onDeleteControlUI((id) => callbackRefs.current.deleteControlUIRef?.(id, type)));
    }
    if (resetControlDataRef && type) {
      cleanups.push(
        onResetControlData((ids) => callbackRefs.current.resetControlDataRef?.(ids, type)),
      );
    }

    return () => cleanups.forEach((off) => off());
  }, [type, controlSettingUI]);

  return {
    emitControlChange,
    emitControlUIChange,
    emitDeleteControlUI,
    emitResetControlData,
    onControlChange,
    onControlUIChange,
    onDeleteControlUI,
    onResetControlData,
  };
};
